import { MemberStatus } from '@prisma/client';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Table } from '@tanstack/react-table';
import { Button, Input } from '@zond/ui';
import { useTranslation } from 'react-i18next';
import { userTypes } from '../data/data';
import { DataTableFacetedFilter } from './data-table-faceted-filter';
import { DataTableViewOptions } from './data-table-view-options';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({ table }: DataTableToolbarProps<TData>) {
  const { t } = useTranslation('settings');
  const isFiltered = table.getState().columnFilters.length > 0;

  const roleOptions = userTypes.map((option) => ({
    ...option,
    label: t(`members.roles.${option.value.toLowerCase()}`),
  }));

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        <Input
          placeholder={t('members.toolbar.filterPlaceholder')}
          value={(table.getColumn('username')?.getFilterValue() as string) ?? ''}
          onChange={(event) => table.getColumn('username')?.setFilterValue(event.target.value)}
          className="h-8 w-[150px] lg:w-[250px]"
        />
        {table.getColumn('status') && (
          <DataTableFacetedFilter
            column={table.getColumn('status')}
            title={t('members.toolbar.status')}
            options={[
              {
                label: t('members.status.active'),
                value: MemberStatus.Active,
              },
              {
                label: t('members.status.invited'),
                value: MemberStatus.Invited,
              },
              {
                label: t('members.status.inactive'),
                value: MemberStatus.Inactive,
              },
            ]}
          />
        )}
        {table.getColumn('role') && (
          <DataTableFacetedFilter
            column={table.getColumn('role')}
            title={t('members.toolbar.role')}
            options={roleOptions}
          />
        )}
        {isFiltered && (
          <Button variant="ghost" onClick={() => table.resetColumnFilters()} className="h-8 px-2 lg:px-3">
            {t('common:actions.reset')}
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}
